import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Button,
  Skeleton,
  Card,
  PageHeader,
  Badge,
  MuteErrorModal,
  BreadcrumbsTimeline,
} from '../components'
import { errorsApi, teamsApi } from '../api'
import type { TeamMember } from '../api/teams'
import type { ErrorGroup, ErrorInstance, ErrorGroupStatus } from '../types'
import { ERROR_GROUP_STATUSES } from '../types'
import { useAuth } from '@roselabs-io/saas-shell'
import {
  AlertTriangle,
  Clock,
  Hash,
  User,
  UserCheck,
  Users,
  Globe,
  Tag,
  ChevronDown,
  ChevronRight,
  Code,
  FileJson,
  CheckCircle,
  BellOff,
  EyeOff,
  RotateCcw,
} from 'lucide-react'
import toast from 'react-hot-toast'

const STATUS_LABELS: Record<ErrorGroupStatus, string> = {
  open: 'Open',
  resolved: 'Resolved',
  ignored: 'Ignored',
  muted: 'Muted',
  regressed: 'Regressed',
}

const STATUS_COLORS: Record<ErrorGroupStatus, string> = {
  open: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700',
  ignored: 'bg-slate-100 text-slate-600',
  muted: 'bg-amber-100 text-amber-700',
  regressed: 'bg-orange-100 text-orange-700',
}

export default function ErrorDetailPage() {
  const { teamId, appId, groupId } = useParams<{ teamId: string; appId: string; groupId: string }>()
  const { user, isLoading: authLoading } = useAuth()
  const [errorGroup, setErrorGroup] = useState<ErrorGroup | null>(null)
  const [instances, setInstances] = useState<ErrorInstance[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedInstances, setExpandedInstances] = useState<Set<string>>(new Set())
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [muteModalOpen, setMuteModalOpen] = useState(false)

  useEffect(() => {
    if (!appId || !groupId || authLoading || !user) return

    const loadData = async () => {
      try {
        const [group, instancesData] = await Promise.all([
          errorsApi.getGroup(appId, groupId),
          errorsApi.listInstances(appId, groupId, { limit: 50 }),
        ])
        setErrorGroup(group)
        setInstances(instancesData.errors)
        // Expand first instance by default
        if (instancesData.errors.length > 0) {
          setExpandedInstances(new Set([instancesData.errors[0].id]))
        }
      } catch (err) {
        console.error('Failed to load error data:', err)
        toast.error('Failed to load error details')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [appId, groupId, authLoading, user])

  // Load team members for assignee dropdown
  useEffect(() => {
    if (!teamId || authLoading || !user) return
    const loadTeamMembers = async () => {
      try {
        const response = await teamsApi.getMembers(teamId)
        setTeamMembers(response.members)
      } catch (err) {
        console.error('Failed to load team members:', err)
      }
    }
    loadTeamMembers()
  }, [teamId, authLoading, user])

  const handleStatusChange = async (newStatus: ErrorGroupStatus) => {
    if (!appId || !groupId) return
    try {
      const updated = await errorsApi.updateStatus(appId, groupId, newStatus)
      setErrorGroup(updated)
      toast.success(`Status changed to ${STATUS_LABELS[newStatus]}`)
    } catch {
      toast.error('Failed to update status')
    }
  }

  const handleAssign = async (assignedTo: string | null) => {
    if (!appId || !groupId) return
    try {
      const updated = await errorsApi.assign(appId, groupId, assignedTo)
      setErrorGroup(updated)
      const memberName = assignedTo
        ? teamMembers.find(m => m.user_id === assignedTo)?.name || 'member'
        : null
      toast.success(memberName ? `Assigned to ${memberName}` : 'Unassigned')
    } catch {
      toast.error('Failed to assign error')
    }
  }

  const handleMute = async (options: { hours?: number; until_count?: number }) => {
    if (!appId || !groupId) return
    try {
      const updated = await errorsApi.mute(appId, groupId, options)
      setErrorGroup(updated)
      const duration = options.hours
        ? `for ${options.hours} hour${options.hours !== 1 ? 's' : ''}`
        : `until ${options.until_count} more occurrences`
      toast.success(`Error muted ${duration}`)
    } catch {
      toast.error('Failed to mute error')
    }
  }

  const toggleInstance = (instanceId: string) => {
    setExpandedInstances(prev => {
      const next = new Set(prev)
      if (next.has(instanceId)) {
        next.delete(instanceId)
      } else {
        next.add(instanceId)
      }
      return next
    })
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString()
  }

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return `${diffDays}d ago`
  }

  if (isLoading) {
    return (
      <>
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-4 w-48 mb-8" />
        <div className="space-y-4">
          <Skeleton className="h-32 rounded-xl" />
          <Skeleton className="h-48 rounded-xl" />
        </div>
      </>
    )
  }

  if (!errorGroup) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Error not found</h2>
          <Link to={`/t/${teamId}/apps/${appId}`}>
            <Button variant="primary">Back to App</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title={errorGroup.message}
        subtitle={`${errorGroup.occurrence_count} occurrences`}
        useHistoryBack
        backLabel="Go back"
        action={
          <div className="flex items-center gap-2">
            <select
              value={errorGroup.assigned_to || ''}
              onChange={e => handleAssign(e.target.value || null)}
              className="border border-slate-300 rounded-lg px-3 py-2 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            >
              <option value="">Unassigned</option>
              {teamMembers.map(member => (
                <option key={member.user_id} value={member.user_id}>
                  {member.name}
                  {member.user_id === user?.id ? ' (me)' : ''}
                </option>
              ))}
            </select>
            <select
              value={errorGroup.status}
              onChange={e => handleStatusChange(e.target.value as ErrorGroupStatus)}
              className="border border-slate-300 rounded-lg px-4 py-2 bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent"
            >
              {ERROR_GROUP_STATUSES.map(s => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
            {errorGroup.status !== 'muted' && (
              <Button
                variant="outline"
                onClick={() => setMuteModalOpen(true)}
                className="flex items-center gap-2"
              >
                <BellOff className="w-4 h-4" />
                Mute
              </Button>
            )}
          </div>
        }
      />

      {/* Error summary card */}
      <Card className="p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Level</p>
              <Badge variant="error">{errorGroup.level}</Badge>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Hash className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Status</p>
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[errorGroup.status]}`}
              >
                {errorGroup.status === 'resolved' && <CheckCircle className="w-3 h-3" />}
                {errorGroup.status === 'muted' && <BellOff className="w-3 h-3" />}
                {errorGroup.status === 'ignored' && <EyeOff className="w-3 h-3" />}
                {errorGroup.status === 'regressed' && <RotateCcw className="w-3 h-3" />}
                {STATUS_LABELS[errorGroup.status]}
              </span>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">First Seen</p>
              <p className="text-sm text-slate-900">
                {formatRelativeTime(errorGroup.first_seen_at)}
              </p>
              <p className="text-xs text-slate-500">{formatDate(errorGroup.first_seen_at)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Last Seen</p>
              <p className="text-sm text-slate-900">
                {formatRelativeTime(errorGroup.last_seen_at)}
              </p>
              <p className="text-xs text-slate-500">{formatDate(errorGroup.last_seen_at)}</p>
            </div>
          </div>
        </div>

        {/* Occurrences and Users stats */}
        <div className="mt-6 pt-6 border-t border-slate-200 grid grid-cols-2 gap-6">
          <div className="flex items-center gap-3">
            <Hash className="w-5 h-5 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Occurrences</p>
              <p className="text-lg font-semibold text-slate-900">
                {errorGroup.occurrence_count.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-slate-400" />
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Users Affected</p>
              <p className="text-lg font-semibold text-slate-900">
                {errorGroup.users_affected_count > 0
                  ? errorGroup.users_affected_count.toLocaleString()
                  : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Assigned To section */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <div className="flex items-center gap-3">
            {errorGroup.assigned_to ? (
              <>
                <UserCheck className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Assigned To</p>
                  <p className="text-sm text-slate-900 font-medium">
                    {teamMembers.find(m => m.user_id === errorGroup.assigned_to)?.name ||
                      'Team Member'}
                    {errorGroup.assigned_to === user?.id && (
                      <span className="ml-1 text-xs text-slate-500">(you)</span>
                    )}
                  </p>
                </div>
              </>
            ) : (
              <>
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Assigned To</p>
                  <p className="text-sm text-slate-500">Unassigned</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Muted info section */}
        {errorGroup.status === 'muted' &&
          (errorGroup.muted_until || errorGroup.ignore_until_count) && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-3">
                <BellOff className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Muted</p>
                  <p className="text-sm text-amber-700 font-medium">
                    {errorGroup.muted_until
                      ? `Until ${new Date(errorGroup.muted_until).toLocaleString()}`
                      : errorGroup.ignore_until_count
                        ? `Until ${errorGroup.ignore_until_count} more occurrences`
                        : 'Muted'}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStatusChange('open')}
                  className="ml-auto"
                >
                  Unmute
                </Button>
              </div>
            </div>
          )}

        {errorGroup.environments?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-slate-400" />
              <p className="text-xs text-slate-500 uppercase tracking-wide">Environments</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {errorGroup.environments.map(env => (
                <span
                  key={env}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    env === 'production'
                      ? 'bg-red-100 text-red-700'
                      : env === 'staging'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {env}
                </span>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Error instances */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-slate-900">
          Error Instances ({instances.length})
        </h3>
        <p className="text-sm text-slate-500">Click to expand and view stack trace and context</p>
      </div>

      <div className="space-y-3">
        {instances.map(instance => {
          const isExpanded = expandedInstances.has(instance.id)
          return (
            <Card key={instance.id} className="overflow-hidden">
              <button
                onClick={() => toggleInstance(instance.id)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-accent-50/50 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900">
                      {formatRelativeTime(instance.created_at)}
                    </span>
                    {instance.environment && (
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs ${
                          instance.environment === 'production'
                            ? 'bg-red-100 text-red-700'
                            : instance.environment === 'staging'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {instance.environment}
                      </span>
                    )}
                    {instance.release && (
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Tag className="w-3 h-3" />
                        {instance.release}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{formatDate(instance.created_at)}</p>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  {instance.user_id_context && (
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      {instance.user_id_context}
                    </span>
                  )}
                  {instance.ip_address && (
                    <span className="flex items-center gap-1">
                      <Globe className="w-3.5 h-3.5" />
                      {instance.ip_address}
                    </span>
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-slate-200">
                  {/* Message */}
                  <div className="p-4 bg-slate-50">
                    <p className="text-sm text-slate-700 font-medium">{instance.message}</p>
                  </div>

                  {/* Stack trace */}
                  {instance.stack_trace && (
                    <div className="border-t border-slate-200">
                      <div className="flex items-center gap-2 px-4 py-2 bg-slate-100">
                        <Code className="w-4 h-4 text-slate-500" />
                        <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                          Stack Trace
                        </span>
                      </div>
                      <pre className="p-4 text-xs overflow-x-auto bg-slate-50 text-slate-700 font-mono leading-relaxed">
                        {instance.stack_trace}
                      </pre>
                    </div>
                  )}

                  {/* Context */}
                  {instance.context && Object.keys(instance.context).length > 0 && (
                    <div className="border-t border-slate-200">
                      <div className="flex items-center gap-2 px-4 py-2 bg-slate-100">
                        <FileJson className="w-4 h-4 text-slate-500" />
                        <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                          Context
                        </span>
                      </div>
                      <pre className="p-4 text-xs overflow-x-auto bg-slate-50 text-slate-700 font-mono">
                        {JSON.stringify(instance.context, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Breadcrumbs */}
                  {instance.breadcrumbs && instance.breadcrumbs.length > 0 && (
                    <BreadcrumbsTimeline
                      breadcrumbs={instance.breadcrumbs}
                      errorTimestamp={instance.created_at}
                    />
                  )}

                  {/* Metadata */}
                  <div className="border-t border-slate-200 p-4 bg-slate-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      {instance.user_id_context && (
                        <div>
                          <span className="text-slate-500">User ID:</span>
                          <span className="ml-2 text-slate-900 font-medium">
                            {instance.user_id_context}
                          </span>
                        </div>
                      )}
                      {instance.ip_address && (
                        <div>
                          <span className="text-slate-500">IP Address:</span>
                          <span className="ml-2 text-slate-900 font-medium">
                            {instance.ip_address}
                          </span>
                        </div>
                      )}
                      {instance.environment && (
                        <div>
                          <span className="text-slate-500">Environment:</span>
                          <span className="ml-2 text-slate-900 font-medium">
                            {instance.environment}
                          </span>
                        </div>
                      )}
                      {instance.release && (
                        <div>
                          <span className="text-slate-500">Release:</span>
                          <span className="ml-2 text-slate-900 font-medium">
                            {instance.release}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )
        })}

        {instances.length === 0 && (
          <Card className="p-8 text-center">
            <p className="text-slate-500">No error instances found</p>
          </Card>
        )}
      </div>

      <MuteErrorModal
        isOpen={muteModalOpen}
        onClose={() => setMuteModalOpen(false)}
        errorMessage={errorGroup.message}
        onMute={handleMute}
      />
    </>
  )
}
